import { type Point, Rect } from "@signal-app/geometry"
import { useCallback, useMemo } from "react"
import { useTickScroll } from "../../../../hooks/useTickScroll"
import { usePianoRoll } from "../../../piano-roll/hooks/usePianoRoll"
import type { ControlCoordTransform } from "../../entities/ControlCoordTransform"
import { useDragSelectionGesture } from "../../gestures/useDragSelectionGesture"
import { useControlPane } from "../../hooks/useControlPane"
import { LineGraphItems } from "./LineGraphItems"

export const ControlLineGraphItems = ({
  items,
  zIndex,
  width,
  lineWidth,
  circleRadius,
  controlTransform,
}: {
  items: {
    x: number
    y: number
    id: number
  }[]
  zIndex: number
  width: number
  lineWidth: number
  circleRadius: number
  controlTransform: ControlCoordTransform
}) => {
  const { selectedEventIds } = useControlPane()
  const { mouseMode } = usePianoRoll()
  const { scrollLeft } = useTickScroll()
  const dragSelectionGesture = useDragSelectionGesture()

  const controlPoints = useMemo(
    () =>
      items.map((p) => ({
        ...Rect.fromPointWithSize(p, circleRadius * 2),
        id: p.id,
      })),
    [items, circleRadius],
  )

  const getLocal = useCallback(
    (e: MouseEvent): Point => ({
      x: e.offsetX + scrollLeft,
      y: e.offsetY,
    }),
    [scrollLeft],
  )

  const handleMouseDownItem = useCallback(
    (e: MouseEvent, hitEventId: number) => {
      if (mouseMode !== "selection") {
        return
      }
      e.stopPropagation()
      const local = getLocal(e)
      dragSelectionGesture(e, hitEventId, local, controlTransform)
    },
    [mouseMode, dragSelectionGesture, getLocal, controlTransform],
  )

  return (
    <LineGraphItems
      scrollLeft={scrollLeft}
      width={width}
      items={items}
      selectedEventIds={selectedEventIds}
      controlPoints={controlPoints}
      lineWidth={lineWidth}
      zIndex={zIndex}
      onMouseDownItem={handleMouseDownItem}
    />
  )
}
